import { useState } from 'react';

interface Player {
  firstName: string;
  lastName: string;
  email: string;
  ghin: string;
}

export default function MultipleRegistrationForm() {
  const [player, setPlayer] = useState<Player>({
    firstName: '',
    lastName: '',
    email: '',
    ghin: ''
  });
  const [numberOfPlayers, setNumberOfPlayers] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handlePlayerChange = (field: keyof Player, value: string) => {
    setPlayer(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberOfPlayersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setNumberOfPlayers(0);
    } else {
      const num = parseInt(value);
      if (!isNaN(num) && num >= 1 && num <= 12) {
        setNumberOfPlayers(num);
      } else if (!isNaN(num) && num > 12) {
        setNumberOfPlayers(12);
      }
    }
  };

  const handleNumberOfPlayersFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔵 STEP 1: Form submitted');
    
    if (numberOfPlayers < 1 || numberOfPlayers > 12) {
      setSubmitMessage('Please enter a valid number of players (1-12)');
      return;
    }

    // Validate that we have the main player's info
    if (!player.firstName || !player.lastName || !player.email) {
      setSubmitMessage('Please enter your contact information');
      return;
    }

    console.log('🔵 STEP 2: Validation passed');

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const functionUrl = `${supabaseUrl}/functions/v1/create-checkout-alaqua`;
      
      console.log('🔵 STEP 3: Supabase URL:', supabaseUrl);
      console.log('🔵 STEP 4: Full Function URL:', functionUrl);
      console.log('🔵 STEP 5: Payload:', {
        players: [player],
        numberOfPlayers: numberOfPlayers
      });

      console.log('🔵 STEP 6: Making fetch request...');

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          players: [player],
          numberOfPlayers: numberOfPlayers
        }),
      });

      console.log('🔵 STEP 7: Response received - Status:', response.status);
      console.log('🔵 STEP 8: Response headers:', Object.fromEntries(response.headers.entries()));
      
      const responseText = await response.text();
      console.log('🔵 STEP 9: Raw response text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('🔵 STEP 10: Parsed JSON:', data);
      } catch (parseError) {
        console.error('❌ Failed to parse JSON:', parseError);
        console.error('❌ Response was:', responseText);
        setSubmitMessage(`Error: Invalid response from server. Please try again.`);
        setIsSubmitting(false);
        return;
      }

      if (data.emailSent) {
        console.log('✅ EMAIL SENT SUCCESSFULLY!');
      } else if (data.emailError) {
        console.error('⚠️ EMAIL FAILED:', data.emailError);
      }

      if (data.error) {
        console.error('❌ Error from Edge Function:', data.error);
        setSubmitMessage(`Error: ${data.error}`);
        setIsSubmitting(false);
        return;
      }

      if (data.url) {
        console.log('🔵 STEP 11: Redirecting to Stripe:', data.url);
        window.location.href = data.url;
      } else {
        console.error('❌ No URL in response');
        setSubmitMessage('Error: No payment URL received. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('❌ FETCH ERROR:', error);
      console.error('❌ Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      setSubmitMessage(`An error occurred: ${error instanceof Error ? error.message : 'Please try again'}`);
      setIsSubmitting(false);
    }
  };

  const totalAmount = numberOfPlayers * 225;

  return (
    <section id="register-multiple" className="py-16 sm:py-20 md:py-24 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight px-2">
            Register for Alaqua CC
          </h2>
          <p className="text-base sm:text-lg text-emerald-600 font-semibold mb-2">
            January 23rd, 2026 - 10am Tee Times
          </p>
          <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 px-4">
            Register up to 12 players at once - $225 per player
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg p-4 sm:p-6 md:p-8 shadow-sm">
          {/* Number of Players to Pay For */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-emerald-50 rounded-lg">
            <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-2 leading-tight">
              How many players are you paying for? <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              max="12"
              value={numberOfPlayers || ''}
              onChange={handleNumberOfPlayersChange}
              onFocus={handleNumberOfPlayersFocus}
              required
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm sm:text-base"
            />
            <p className="text-xs sm:text-sm text-gray-600 mt-2">
              Total Amount: <span className="font-bold text-emerald-600 text-base sm:text-lg">${totalAmount}</span> ({numberOfPlayers} player{numberOfPlayers !== 1 ? 's' : ''} × $225)
            </p>
          </div>

          {/* Contact Information */}
          <div className="mb-6 sm:mb-8">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Your Contact Information</h3>
            
            <div className="p-4 sm:p-6 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={player.firstName}
                    onChange={(e) => handlePlayerChange('firstName', e.target.value)}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={player.lastName}
                    onChange={(e) => handlePlayerChange('lastName', e.target.value)}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={player.email}
                    onChange={(e) => handlePlayerChange('email', e.target.value)}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    GHIN (Optional)
                  </label>
                  <input
                    type="text"
                    value={player.ghin}
                    onChange={(e) => handlePlayerChange('ghin', e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Message */}
          {submitMessage && (
            <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-md text-sm sm:text-base ${submitMessage.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
              {submitMessage}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-emerald-600 text-white py-3 sm:py-4 px-6 sm:px-8 rounded-md text-base sm:text-lg font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? 'Processing...' : `Proceed to Payment - $${totalAmount}`}
          </button>

          <p className="text-center text-xs sm:text-sm text-gray-600 mt-3 sm:mt-4">
            You will be redirected to Stripe's secure payment page
          </p>
        </form>
      </div>
    </section>
  );
}
